import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import Cookies from "js-cookie";
import { ParagraphText, UBAppModal, UBOTPInput } from "../../../../Libraries";
import { handleApi } from "../../../../apis";
import {
  ACCOUNT_PIN_RESET,
  CHECK_PIN_GET,
} from "../../../../apis/configuration";
import { useAuthContext } from "../../../../context/AuthContext";
import { useWalletContext } from "../../../../context/WalletContext";
import { LanguageContext } from "../../../../context/LanguageContext";
import { useSnackbar } from "../../../../context/SnackbarContext";
import { clearLocalStorageExcludeSomeFields } from "../../../../utils";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import styles from "./RNPVerifyModal.module.css";

const RNPVerifyModal = ({ open, onVerified }) => {
  const [I18n] = useContext(LanguageContext);
  const [error, setError] = useState("");
  const [PIN, setPIN] = useState("");
  const [loading, setLoading] = useState(false);
  const { setPinVerified, handleLogoutInDB } = useAuthContext();
  const { KYCVerified } = useWalletContext();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const isDisabled = PIN.length !== 4 || loading;
  const otpContainerRef = useRef(null);

  const focusFirstInput = () => {
    setTimeout(() => {
      const el = otpContainerRef.current?.querySelector("input:not([disabled])");
      if (el) el.focus();
    }, 100);
  };

  useEffect(() => {
    if (open) {
      setPIN("");
      setError("");
      const t = setTimeout(() => {
        const el = otpContainerRef.current?.querySelector("input:not([disabled])");
        if (el) el.focus();
      }, 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  const handleLockedAccount = (message) => {
    showSnackbar({
      message: message || "Your account has been locked please contact support",
      severity: "error",
    });
    const phoneNumber = localStorage.getItem("phone_number");
    clearLocalStorageExcludeSomeFields();
    handleLogoutInDB(navigate);
    if (!KYCVerified) {
      navigate("/ForgotPin", { state: { phone: phoneNumber } });
    } else {
      navigate("/", { state: { isVerifyAccountScreen: true } });
    }
  };

  const handleSubmit = async () => {
    if (isDisabled) return;
    try {
      setError("");
      setLoading(true);
      const response = await handleApi(
        CHECK_PIN_GET,
        { rnp_verify: true, pin: PIN },
        null,
        navigate,
        { "Web-Token": Cookies.get("deviceId") },
      );
      setLoading(false);
      if (response?.success) {
        sessionStorage.removeItem("verifyPinInStack");
        setPinVerified(true);
        window.dispatchEvent(new CustomEvent("rnp-verified"));
        setPIN("");
        onVerified?.();
      } else {
        setPIN("");
        focusFirstInput();
        if (response?.data?.is_locked) {
          handleLockedAccount(response?.data?.message);
          return;
        }
        if (response?.message && response?.attempts_left) {
          setError(
            `${response.message} (${response.attempts_left} ${I18n.tries_left})`,
          );
        }
      }
    } catch (err) {
      setLoading(false);
      setPIN("");
      focusFirstInput();
      const data = err?.response?.data;
      if (data?.is_locked) {
        handleLockedAccount(data?.message);
      }
    }
  };

  const handleForgotPIN = async () => {
    const phone = localStorage.getItem("phone_number");
    try {
      setLoading(true);
      triggerLogEvent(analytics, "w_forgot_pin");
      const response = await handleApi(
        ACCOUNT_PIN_RESET,
        { phone_number: phone },
        null,
        navigate,
        { "Api-Key": process.env.REACT_APP_API_KEY },
      );
      const isVerified = response?.verified;
      if (Boolean(isVerified)) {
        sessionStorage.setItem(
          "forgotPinVerify",
          JSON.stringify({ phone: phone || "" }),
        );
      }
      await handleLogoutInDB(navigate);
      if (Boolean(isVerified)) {
        navigate("/", {
          replace: true,
          state: { isVerifyAccountScreen: true, phoneNumber: phone },
        });
      } else {
        navigate("/ForgotPin", { state: { phone: phone } });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <UBAppModal
      id="rnp_verify_modal"
      size="lg"
      className={styles.modalRoot}
      open={open}
      onClose={() => {}}
      sx={{ zIndex: 10000 }}
      disableAutoFocus
      header={{
        title: (
          <ParagraphText className="d-flex align-items-center fontSize20 fontWeight500 newFontColor">
            {I18n.verify_PIN}
          </ParagraphText>
        ),
        dont_show_cross_icon: true,
      }}
      footer={[
        {
          data: {
            id: "rnp_verify_confirm_btn",
            className: `ubPrimaryButton ${styles.verifyBtn}`,
            title: I18n.verify_PIN,
            onClick: handleSubmit,
            disabled: isDisabled,
          },
        },
      ]}
    >
      <Box
        className={styles.body}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !isDisabled) handleSubmit();
        }}
      >
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              backgroundColor: "var(--modalLoadingOverlay)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
            }}
          >
            <CircularProgress size={32} sx={{ color: "var(--mainColor)" }} />
          </Box>
        )}
        <div ref={otpContainerRef}>
          <UBOTPInput
            label={I18n.enter_pin}
            className={styles.otpInput}
            value={PIN}
            onChangeQuery={(val) => setPIN(val)}
            invalid={!!error}
            helperText={error}
            isInputSecure={true}
            id="rnp_verify_pin_input"
            disabled={loading}
          />
        </div>
        <ParagraphText
          className={`fontSize16 ${styles.forgotPin}`}
          onClick={handleForgotPIN}
          id="rnp_modal_forgot_pin_link"
        >
          {I18n.forgot_pin}
        </ParagraphText>
      </Box>
    </UBAppModal>
  );
};

export default RNPVerifyModal;
